import React, { useEffect, useId, useState } from "react";

import * as styles from "./SearchableSelect.module.css";

export type SearchableSelectOption = Readonly<{
  value: string;
  /** Shown in the unfiltered list, indented by depth. */
  label: string;
  /** Full text shown in the input and in search results; also what search matches. */
  path: string;
  /** Tree depth for indentation; 0-2 supported. */
  depth: number;
}>;

type SearchableSelectProps = Readonly<{
  id: string;
  label: string;
  placeholder: string;
  noResultsLabel: string;
  value: string;
  options: ReadonlyArray<SearchableSelectOption>;
  onChange: (value: string) => void;
  required?: boolean;
  className?: string;
}>;

const depthClasses = [styles.depth0, styles.depth1, styles.depth2];
const matches = (option: SearchableSelectOption, search: string) =>
  option.path.toLocaleLowerCase().includes(search);

/** Keeps matching options plus their ancestors; options must be in depth-first tree order. */
const filterTree = (options: ReadonlyArray<SearchableSelectOption>, search: string) => {
  const keep = options.map(option => matches(option, search));
  let childDepth = 0;
  for (let index = options.length - 1; index >= 0; index--) {
    const { depth } = options[index];
    keep[index] ||= depth < childDepth;
    childDepth = keep[index] ? depth : childDepth;
  }
  return options.filter((_, index) => keep[index]);
};

/** Editable combobox with list autocomplete (WAI-ARIA APG pattern). */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id,
  label,
  placeholder,
  noResultsLabel,
  value,
  options,
  onChange,
  required,
  className = "",
}) => {
  const listId = `${useId()}-list`;
  const [query, setQuery] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const selected = options.find(option => option.value === value);
  const search = query?.trim().toLocaleLowerCase();
  const visible = search ? filterTree(options, search) : options;
  const active = isOpen ? visible[activeIndex] : undefined;
  const activeId = active ? `${listId}-${active.value}` : undefined;

  useEffect(() => {
    if (activeId) {
      document.getElementById(activeId)?.scrollIntoView?.({ block: "nearest" });
    }
  }, [activeId]);

  const open = () => {
    if (!isOpen) {
      setIsOpen(true);
      setActiveIndex(Math.max(selected ? visible.indexOf(selected) : 0, 0));
    }
  };
  const close = () => {
    setIsOpen(false);
    setQuery(null);
  };
  const choose = (option: SearchableSelectOption) => {
    onChange(option.value);
    close();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        open();
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex(index => Math.min(Math.max(index + step, 0), visible.length - 1));
    } else if (event.key === "Enter" && active) {
      event.preventDefault();
      choose(active);
    } else if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      close();
    }
  };

  return (
    <div className={`${styles.wrapper} ${className}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={isOpen}
        aria-activedescendant={activeId}
        required={required}
        placeholder={placeholder}
        value={query ?? selected?.path ?? ""}
        onClick={open}
        onBlur={close}
        onKeyDown={onKeyDown}
        onChange={event => {
          const next = event.target.value;
          const nextSearch = next.trim().toLocaleLowerCase();
          setQuery(next);
          setIsOpen(true);
          setActiveIndex(
            Math.max(
              filterTree(options, nextSearch).findIndex(option => matches(option, nextSearch)),
              0
            )
          );
          if (value) {
            onChange("");
          }
        }}
      />
      <ul
        id={listId}
        role="listbox"
        className={styles.list}
        hidden={!isOpen}
        // Keep focus in the input so clicking an option doesn't blur-close first.
        onMouseDown={event => event.preventDefault()}
      >
        {visible.map((option, index) => (
          // Keyboard selection is handled by the combobox input (aria-activedescendant).
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <li
            key={option.value}
            id={`${listId}-${option.value}`}
            role="option"
            aria-selected={option.value === value}
            className={[
              styles.option,
              depthClasses[option.depth],
              index === activeIndex ? styles.active : "",
            ].join(" ")}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => choose(option)}
          >
            {option.label}
          </li>
        ))}
        {!visible.length && <li className={styles.empty}>{noResultsLabel}</li>}
      </ul>
    </div>
  );
};
